import { UPGRADE_OFFER_FEATURE, UPGRADE_OFFERS, UpgradeOffer } from '@pubstack/common/src/upgrade'
import { ScopeWithFeatures } from '~/hooks/useScope'

/**
 * Whether the **scope** owns an offer — the one question every upgrade pitch in the app hangs off.
 *
 * Asked of the scope rather than the viewer on purpose: a Pubstack user has every feature synthesised
 * for whichever account they are in, so `user.hasFeature` here would hide from staff the very thing
 * they most need to see, which is what the customer gets. Customers are unaffected — their own
 * features are the scope's.
 *
 * Both products are access features: `adstack` / `pbsStandalone` being granted is what makes them owned. A scope
 * onboarded on Node whose users have no access yet does NOT own Node here — that is the staff-only state, where
 * Pubstack admins alone see Node. It is a viewer question, so it is answered by `useNodeStaffAccess` (or
 * `nodeStaffState`), never by this scope-level predicate.
 *
 * This answers *ownership*, never *permission*. Whether a write is allowed is a separate question with
 * a separate answer: creating a bidder seat needs the **access** feature, because a scope can be
 * provisioned before its users are granted anything (the admin scope page carries the two toggles side
 * by side, and they are set independently).
 */
export const scopeOwnsOffer = (scope: ScopeWithFeatures, offer: UpgradeOffer): boolean => scope.hasFeature(offer === 'one' ? UPGRADE_OFFER_FEATURE.one : UPGRADE_OFFER_FEATURE.node)

/** A scope on neither monetisation product: the audience every "subscribe" page is written for. */
export const scopeOwnsNoOffer = (scope: ScopeWithFeatures): boolean => !UPGRADE_OFFERS.some((offer) => scopeOwnsOffer(scope, offer))
